import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ChatStore } from "@/stores/ChatStore";
import { toast } from "sonner";
import type { TBrokerInfo } from "@/types/TBrokerInfo";
import { CheckCircle, XCircle } from "lucide-react";
import { getVSNRValidationStatus } from "@/lib/vsnr-validation";

function generateBrokerMessage(brokerData: TBrokerInfo): string {
  const parts: string[] = [
    `Ich habe Ihren Vermittler gefunden.`
  ];

  if (brokerData.name) {
    parts.push(`Das ist ${brokerData.name}.`);
  }

  parts.push("Die Kontaktdaten finden Sie im eingeblendeten Fenster.");

  return parts.join(" ");
}

export function BrokerValidationDialog() {
  const isOpen = ChatStore(state => state.brokerValidationDialogOpen);
  const setBrokerInfo = ChatStore(state => state.setBrokerInfo);
  const setDialogOpen = ChatStore(state => state.setBrokerValidationDialogOpen);

  const [vsnr, setVsnr] = useState("");
  const [vsnrValidation, setVsnrValidation] = useState<{ isValid: boolean; formatted: string | null; message: string }>({ isValid: false, formatted: null, message: "" });
  const [plz, setPlz] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Validate VSNR on change
  useEffect(() => {
    const status = getVSNRValidationStatus(vsnr);
    setVsnrValidation(status);
  }, [vsnr]);

  const resetForm = () => {
    setVsnr("");
    setPlz("");
    setVsnrValidation({ isValid: false, formatted: null, message: "" });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Use formatted VSNR if available, otherwise use raw input
    const vsnrToSubmit = vsnrValidation.formatted || vsnr.trim();

    if (!vsnrToSubmit) {
      toast.error("Bitte geben Sie die Versicherungsscheinnummer ein");
      return;
    }

    if (!plz.trim()) {
      toast.error("Bitte geben Sie die Postleitzahl ein");
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch("/api/multiadapter/validate", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          vsnr: vsnrToSubmit,
          plz: plz.trim(),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.error || "Validierung fehlgeschlagen";
        toast.error(errorMessage);
        return;
      }

      const brokerData = await response.json();

      if (brokerData.valid) {
        setBrokerInfo(brokerData);
        setDialogOpen(false);
        resetForm();
        toast.success("Vermittler-Informationen geladen");

        // Generate avatar response message
        const vermittlerMessage = generateBrokerMessage(brokerData);

        // Add message to chat history
        ChatStore.getState().addMessage({
          id: crypto.randomUUID(),
          sender: "assistant",
          transcript: vermittlerMessage,
          timestamp: new Date()
        });

        // Append to delta buffer (for avatar speech if available)
        ChatStore.getState().appendDeltaBuffer(vermittlerMessage);
      } else if (brokerData.blacklisted) {
        // Special case: broker is on the blacklist
        setDialogOpen(false);
        resetForm();

        // Use the message from backend or fallback
        const blacklistMessage = brokerData.message || "Tut mir leid, für diesen Vertrag kann ich den Vermittler leider nicht ermitteln.";

        // Add message to chat history
        ChatStore.getState().addMessage({
          id: crypto.randomUUID(),
          sender: "assistant",
          transcript: blacklistMessage,
          timestamp: new Date()
        });

        // Append to delta buffer (for avatar speech if available)
        ChatStore.getState().appendDeltaBuffer(blacklistMessage);
      } else {
        toast.error("Ungültige VSNR oder PLZ");
      }
    } catch (error) {
      console.error("Broker validation error:", error);
      toast.error("Fehler bei der Validierung");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setDialogOpen(false);
    resetForm();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open: boolean) => {
      setDialogOpen(open);
      if (!open) resetForm();
    }}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Vermittler-Informationen abrufen</DialogTitle>
          <DialogDescription>
            Bitte geben Sie Ihre Versicherungsscheinnummer und Postleitzahl ein.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="vsnr">Versicherungsscheinnummer (VSNR)</Label>
            <div className="relative">
              <Input
                id="vsnr"
                placeholder="z.B. XXXX-04-1234567"
                value={vsnr}
                onChange={(e) => setVsnr(e.target.value)}
                disabled={isLoading}
                className={vsnr.length > 0 ? (vsnrValidation.isValid ? "pr-10 border-green-500" : "pr-10 border-orange-400") : ""}
                required
              />
              {vsnr.length > 0 && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {vsnrValidation.isValid ? (
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  ) : (
                    <XCircle className="h-5 w-5 text-orange-400" />
                  )}
                </div>
              )}
            </div>
            {vsnrValidation.formatted && vsnrValidation.formatted !== vsnr && (
              <p className="text-xs text-green-600">Formatiert: {vsnrValidation.formatted}</p>
            )}
            {vsnrValidation.message && (
              <p className="text-xs text-orange-500">{vsnrValidation.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="plz">Postleitzahl</Label>
            <Input
              id="plz"
              placeholder="z.B. 12345"
              value={plz}
              onChange={(e) => setPlz(e.target.value)}
              disabled={isLoading}
              maxLength={5}
              required
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={isLoading}
            >
              Abbrechen
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? "Validiere..." : "Bestätigen"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
